import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Users, CurrencyGbp, Receipt, Briefcase, MagnifyingGlass, Warning } from "@phosphor-icons/react";

const fmt = (v) => `£${(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2 })}`;

export default function ClientSpendPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    API.get("/reports/client-spend").then(({ data }) => setData(data)).catch(() => toast.error("Failed to load")).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const reports = (data?.reports || []).filter(r =>
    !search || (r.client_name || "").toLowerCase().includes(search.toLowerCase()) || (r.company_name || "").toLowerCase().includes(search.toLowerCase())
  );

  const totalInvoiced = reports.reduce((s, r) => s + r.total_invoiced, 0);
  const totalOutstanding = reports.reduce((s, r) => s + r.outstanding, 0);
  const totalQuoted = reports.reduce((s, r) => s + r.total_quoted, 0);

  return (
    <div data-testid="client-spend-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
          <Users size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Client Spend Reports</h1>
          <p className="text-xs text-muted-foreground">{data?.total || 0} clients</p>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-3 gap-4 mb-6" data-testid="spend-kpis">
        <div className="border border-border rounded-lg p-4 bg-card">
          <p className="text-xs text-muted-foreground">Total Quoted</p>
          <p className="text-lg font-bold font-[Chivo]">{fmt(totalQuoted)}</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <p className="text-xs text-muted-foreground">Total Invoiced</p>
          <p className="text-lg font-bold font-[Chivo] text-emerald-600">{fmt(totalInvoiced)}</p>
        </div>
        <div className="border border-border rounded-lg p-4 bg-card">
          <p className="text-xs text-muted-foreground">Outstanding</p>
          <p className="text-lg font-bold font-[Chivo] text-red-600">{fmt(totalOutstanding)}</p>
        </div>
      </div>

      <div className="relative max-w-sm mb-4">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search clients..." className="pl-9 h-9 text-sm" data-testid="spend-search" />
      </div>

      <div className="border border-border rounded-lg overflow-hidden" data-testid="spend-table">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border bg-muted/30">
                <th className="px-3 py-2.5 text-left font-semibold">Client</th>
                <th className="px-3 py-2.5 text-right font-semibold">Quotes</th>
                <th className="px-3 py-2.5 text-right font-semibold">Quoted Value</th>
                <th className="px-3 py-2.5 text-right font-semibold">Invoices</th>
                <th className="px-3 py-2.5 text-right font-semibold">Invoiced</th>
                <th className="px-3 py-2.5 text-right font-semibold">Paid</th>
                <th className="px-3 py-2.5 text-right font-semibold">Outstanding</th>
                <th className="px-3 py-2.5 text-right font-semibold">Jobs</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(r => (
                <tr key={r.id} className="border-b border-border/50 hover:bg-muted/10 cursor-pointer" onClick={() => navigate(`/clients/${r.id}`)} data-testid={`spend-row-${r.id}`}>
                  <td className="px-3 py-2.5">
                    <p className="font-medium">{r.client_name}</p>
                    {r.company_name && <p className="text-[9px] text-muted-foreground">{r.company_name}</p>}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    <span>{r.total_quotes}</span>
                    {r.approved_quotes > 0 && <span className="text-emerald-600 ml-1">({r.approved_quotes} approved)</span>}
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono">{fmt(r.total_quoted)}</td>
                  <td className="px-3 py-2.5 text-right">{r.total_invoices}</td>
                  <td className="px-3 py-2.5 text-right font-mono font-medium">{fmt(r.total_invoiced)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-emerald-600">{fmt(r.paid_amount)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">
                    {r.outstanding > 0 ? (
                      <span className="text-red-600 font-medium flex items-center justify-end gap-1"><Warning size={12} /> {fmt(r.outstanding)}</span>
                    ) : (
                      <span className="text-muted-foreground">{fmt(0)}</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-right">{r.completed_jobs}/{r.total_jobs}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
